import { FlashMessage } from './FlashMessage';
import { Provider } from './LayoutContext';
import { SIDEBAR_PUSH_THRESHOLD_MEDIA_QUERY, SIDEBAR_TRANSITION, SIDEBAR_WIDTH, } from '../Sidebar';
import { isBrowser } from '../utilities';
import { mb } from '../Themes/utilities';
import { rem } from 'polished';
import HeaderBar from '../HeaderBar';
import Icon from '../Icon';
import ModalContextProvider from './ModalContextProvider';
import Navigation from './Navigation';
import React from 'react';
import RolesConnector from '@sportnet/roles-connector';
import ScrollLayout from '../Layouts/ScrollLayout';
import StatusBar from './StatusBar';
import StatusBarAction from './StatusBarItems/StatusBarAction';
import StatusBarAppSpaceSwitch from './StatusBarItems/StatusBarAppSpaceSwitch';
import StatusBarAppSwitch from './StatusBarItems/StatusBarAppSwitch';
import StatusBarChildApp from './StatusBarItems/StatusBarChildApp';
import StatusBarCloseButton from './StatusBarItems/StatusBarCloseButton';
import StatusBarNotifications from './StatusBarItems/StatusBarNotifications';
import StatusBarUser from './StatusBarItems/StatusBarUser';
import cookies from 'js-cookie';
import styled, { css, useTheme } from 'styled-components';
const NAVIGATION_OPENED_COOKIES_KEY = 'navigation_opened';
const Wrapper = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
`;
const InnerWrapper = styled.div `
  position: relative;
  overflow: hidden;
  display: flex;
  flex: 1 0 0;
  height: 100%;
`;
const Body = styled.div `
  position: relative;
  overflow: hidden;
  display: flex;
  flex: 1 0 0;
  height: 100%;
  background-color: ${({ theme }) => theme.theLayout.backgroundColor};
`;
const Pushable = styled.div `
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  transition: margin-right ${SIDEBAR_TRANSITION},
    margin-left ${SIDEBAR_TRANSITION};
  max-width: 100%;
  height: 100%;
  ${({ $isPushingFromRight }) => $isPushingFromRight &&
    css `
      ${mb(SIDEBAR_PUSH_THRESHOLD_MEDIA_QUERY)} {
        margin-right: ${rem(SIDEBAR_WIDTH)};
      }
    `}
  ${({ $isPushingFromLeft }) => $isPushingFromLeft &&
    css `
      ${mb(SIDEBAR_PUSH_THRESHOLD_MEDIA_QUERY)} {
        margin-left: ${rem(SIDEBAR_WIDTH)};
      }
    `}
`;
const TheLayout = ({ title: titleProps = '', useContext = false, children, className, top, bottom, userName, userPictureSrc, notificationCount, navigation, onLogout, onClose, customNavigation, experimentalStatusBarActions, renderNavigationLink, app, childApp, appSpace, appSpaces, apps, userSubName, accessToken, loggingOut, flashMessage, contentDivider, grant, admin, }) => {
    const theme = useTheme();
    const leftSidebarPlaceholderRef = React.useRef(null);
    const rightSidebarPlaceholderRef = React.useRef(null);
    const [attachedSidebars, setAttachedSidebars] = React.useState([]);
    const [navigationOpened, setNavigationOpened] = React.useState(() => {
        const navigationState = cookies.get(NAVIGATION_OPENED_COOKIES_KEY);
        return !navigationState || navigationState === '1';
    });
    const [title, setTitle] = React.useState(titleProps);
    const [backAction, setBackAction] = React.useState(null);
    const [isRolesManagerOpened, setIsRolesManagerOpened] = React.useState(false);
    React.useEffect(() => {
        cookies.set(NAVIGATION_OPENED_COOKIES_KEY, navigationOpened ? '1' : '0');
        const getWindowTitle = () => {
            return [app?.name || '', appSpace?.name || '']
                .filter(Boolean)
                .join(' | ');
        };
        if (isBrowser()) {
            window.document.title = getWindowTitle();
        }
    }, [navigationOpened, app?.name, appSpace?.name]);
    const closeNavigation = React.useCallback(() => {
        setNavigationOpened(false);
    }, []);
    const closeNavigationMobile = React.useCallback(() => {
        const element = document.querySelector('.Ui-TheLayout-Navigation-Backdrop');
        if (element) {
            if (getComputedStyle(element).opacity === '1') {
                closeNavigation();
            }
        }
    }, [closeNavigation]);
    const onMenuToggle = () => {
        setNavigationOpened(prevState => !prevState);
    };
    const isPushingFromLeft = attachedSidebars
        .filter(sd => !sd.modal && sd.position === 'left')
        .some(sd => !!sd.opened);
    const isPushingFromRight = attachedSidebars
        .filter(sd => !sd.modal && sd.position === 'right')
        .some(sd => !!sd.opened);
    const detachSidebar = React.useCallback((sd) => {
        setAttachedSidebars(prevState => prevState.filter(d => d.id !== sd.id));
    }, []);
    const updateSidebar = React.useCallback((sd) => {
        setAttachedSidebars(prevState => {
            const newAttachedSidebars = [...prevState];
            const existingSidebarIdx = newAttachedSidebars.findIndex(sidebar => sidebar.id === sd.id);
            if (existingSidebarIdx > -1) {
                const updatedItem = {
                    ...newAttachedSidebars[existingSidebarIdx],
                    ...sd,
                };
                newAttachedSidebars.splice(existingSidebarIdx, 1, updatedItem);
            }
            else {
                newAttachedSidebars.push(sd);
            }
            return newAttachedSidebars;
        });
    }, []);
    const unsetTitle = React.useCallback(() => {
        setTitle(titleProps);
    }, [titleProps]);
    const unsetBackAction = React.useCallback(() => {
        setBackAction(null);
    }, []);
    return (React.createElement(Provider, { value: {
            leftSidebarPlaceholderRef,
            rightSidebarPlaceholderRef,
            setTitle,
            unsetTitle,
            setBackAction,
            unsetBackAction,
            detachSidebar,
            closeNavigation,
            closeNavigationMobile,
            updateSidebar,
        } },
        isRolesManagerOpened && (React.createElement(RolesConnector, { opened: isRolesManagerOpened, appSpace: appSpace ? appSpace._id : '', appId: app?._id || '', accessToken: accessToken || '', onClose: () => setIsRolesManagerOpened(false), onError: e => {
                alert(e.message);
                setIsRolesManagerOpened(false);
                throw e;
            }, onUnauthorized: () => {
                setIsRolesManagerOpened(false);
                throw Error('Neautorizovaný pre správu používateľov');
            } })),
        React.createElement(Wrapper, { className: className },
            React.createElement(StatusBar, null,
                !!(navigation || customNavigation) && (React.createElement(StatusBar.Button, { onClick: onMenuToggle },
                    React.createElement(Icon, { name: "burger", size: "l", color: theme.color.primary }))),
                appSpace && (React.createElement(StatusBarAppSpaceSwitch, { appSpace: appSpace, admin: admin, appSpaces: appSpaces, app: app })),
                app && (React.createElement(StatusBarAppSwitch, { app: app, apps: apps, appSpace: appSpace })),
                childApp && (React.createElement(StatusBarChildApp, { childApp: childApp, contentDivider: contentDivider })),
                grant && accessToken && (React.createElement(StatusBar.Button, { onClick: () => {
                        setIsRolesManagerOpened(true);
                    } },
                    React.createElement(Icon, { name: "group", size: "l", color: theme.color.primary }))),
                React.createElement(StatusBar.Spacer, null),
                (experimentalStatusBarActions || []).map((action, idx) => (React.createElement(StatusBarAction, { key: idx, onClick: action.onClick, icon: action.icon, label: action.label }))),
                !!notificationCount && (React.createElement(StatusBarNotifications, { notificationCount: notificationCount })),
                userName && (React.createElement(StatusBarUser, { userName: userName, userSubName: userSubName, userPictureSrc: userPictureSrc, onLogout: onLogout, loggingOut: loggingOut })),
                onClose && React.createElement(StatusBarCloseButton, { onClose: onClose })),
            flashMessage ?? null,
            React.createElement(InnerWrapper, null,
                (navigation || customNavigation) && (React.createElement(Navigation, { items: navigation ? navigation : [], visible: navigationOpened, onClose: closeNavigation, renderLink: renderNavigationLink }, customNavigation)),
                React.createElement(Body, null,
                    React.createElement("div", { ref: leftSidebarPlaceholderRef }),
                    React.createElement(Pushable, { "$isPushingFromLeft": !!isPushingFromLeft, "$isPushingFromRight": !!isPushingFromRight },
                        React.createElement(ScrollLayout, { topFixed: React.createElement(React.Fragment, null,
                                useContext && (!!title || !!backAction) && (React.createElement(HeaderBar, null,
                                    backAction,
                                    title && React.createElement(HeaderBar.Header, null, title))),
                                top), bottomFixed: bottom },
                            React.createElement(ModalContextProvider, null, children))),
                    React.createElement("div", { ref: rightSidebarPlaceholderRef }))))));
};
export default TheLayout;
export { FlashMessage };
